import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ScreenshotUploader } from '../components/ScreenshotUploader';
import { Clock, Mail, LogOut, TrendingUp, CheckCircle2, ExternalLink, MessageCircle, Send } from 'lucide-react';
import { BROKERS, CONTACTS } from '../config/brand';

export default function PendingApproval() {
    const { user, logout, refreshAuth } = useAuth();
    const { t } = useTranslation();
    const userBroker = user?.broker && BROKERS[user.broker];

    const handleUploadSuccess = async () => {
        if (refreshAuth) await refreshAuth();
    };

    return (
        <div className="min-h-screen p-4 py-12 relative" style={{ backgroundColor: 'var(--background-base)' }}>
            <div className="absolute top-4 right-4 z-20">
                <LanguageSwitcher />
            </div>

            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 right-1/3 w-[500px] h-[500px] rounded-full blur-3xl opacity-10" style={{ backgroundColor: 'var(--primary-color)' }}></div>
            </div>

            <div className="relative z-10 w-full max-w-2xl mx-auto">
                <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="logo-link">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center gold-glow" style={{ backgroundColor: 'var(--primary-color)' }}>
                        <TrendingUp className="w-6 h-6" style={{ color: 'var(--primary-text)' }} />
                    </div>
                    <div>
                        <p className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>NIKAYOFX</p>
                        <p className="text-xs tracking-widest" style={{ color: 'var(--text-muted)' }}>PTG</p>
                    </div>
                </Link>

                <div className="card-surface p-8 sm:p-10" data-testid="pending-approval-container">
                    <div className="text-center mb-7">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-gold" style={{ backgroundColor: 'var(--primary-muted)', border: '1px solid var(--border-highlight)' }}>
                            <Clock className="w-8 h-8" style={{ color: 'var(--primary-color)' }} />
                        </div>
                        <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-2" style={{ color: 'var(--primary-color)' }}>
                            {t('pendingApproval.overline')}
                        </p>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="pending-title">
                            {t('pendingApproval.title')}
                        </h1>
                        <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                            Hi {user?.name?.split(' ')[0] || 'there'} — your account is pending review. Speed it up by uploading your deposit proof below.
                        </p>
                    </div>

                    {/* Broker info */}
                    {userBroker && (
                        <div className="rounded-xl p-4 mb-5" style={{ backgroundColor: 'var(--background-elevated)', border: `1px solid ${userBroker.accent}30` }} data-testid="broker-info">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>Your broker</p>
                                    <p className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{userBroker.name}</p>
                                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Min deposit: <span className="font-mono font-semibold" style={{ color: userBroker.accent }}>${userBroker.minDeposit}</span></p>
                                </div>
                                <a
                                    href={userBroker.affiliateUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                                    style={{ backgroundColor: userBroker.accent, color: 'var(--primary-text)' }}
                                    data-testid="reopen-broker-link"
                                >
                                    Open Account
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Steps */}
                    <div className="rounded-xl p-5 mb-6" style={{ backgroundColor: 'var(--background-elevated)', border: '1px solid var(--border-subtle)' }}>
                        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                            <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--primary-color)' }} />
                            What you need to do
                        </h3>
                        <ol className="space-y-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>
                            <li className="flex gap-2">
                                <span className="font-bold" style={{ color: 'var(--primary-color)' }}>1.</span>
                                Open your <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{userBroker?.name || 'broker'}</span> account using the affiliate link above
                            </li>
                            <li className="flex gap-2">
                                <span className="font-bold" style={{ color: 'var(--primary-color)' }}>2.</span>
                                Complete the minimum deposit of <span className="font-mono font-semibold" style={{ color: 'var(--primary-color)' }}>${userBroker?.minDeposit || 50}</span>
                            </li>
                            <li className="flex gap-2">
                                <span className="font-bold" style={{ color: 'var(--primary-color)' }}>3.</span>
                                Upload a screenshot of your deposit proof below
                            </li>
                            <li className="flex gap-2">
                                <span className="font-bold" style={{ color: 'var(--primary-color)' }}>4.</span>
                                We&apos;ll verify and approve your account within 24 hours
                            </li>
                        </ol>
                    </div>

                    {/* Screenshot uploader */}
                    <div className="mb-6">
                        <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Upload your deposit screenshot:</p>
                        <ScreenshotUploader currentUrl={user?.deposit_screenshot_url} onSuccess={handleUploadSuccess} ctaLabel="Upload Deposit Screenshot" />
                    </div>

                    {/* Contact */}
                    <div className="mb-6">
                        <p className="text-xs text-center mb-3" style={{ color: 'var(--text-muted)' }}>Need help? Reach out:</p>
                        <div className="grid grid-cols-2 gap-3">
                            <a href={CONTACTS.whatsapp.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ backgroundColor: '#25D366', color: '#FFFFFF' }} data-testid="contact-whatsapp">
                                <MessageCircle className="w-4 h-4" />
                                WhatsApp
                            </a>
                            <a href={CONTACTS.telegram.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all" style={{ backgroundColor: '#229ED9', color: '#FFFFFF' }} data-testid="contact-telegram">
                                <Send className="w-4 h-4" />
                                Telegram
                            </a>
                        </div>
                    </div>

                    <div className="flex items-center justify-center gap-2 text-sm mb-5 ltr-only" style={{ color: 'var(--text-muted)' }}>
                        <Mail className="w-4 h-4" />
                        <span>{user?.email}</span>
                    </div>

                    <Button onClick={logout} className="btn-secondary w-full" data-testid="pending-logout-btn">
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('common.signOut')}
                    </Button>
                </div>

                <p className="text-center text-sm mt-6" style={{ color: 'var(--text-muted)' }}>
                    {t('pendingApproval.checkBack')}
                </p>
            </div>
        </div>
    );
}
